import { mapProductApiToModel } from '@/features/product/types/mappers/product';
import { formatDate } from '@/utils/util';
import { CartItem, CartResponse } from '../api/cart';
import { CartGroup, CartProduct } from '../models/cart';

/**
 * 날짜만 추출하는 함수 (시간 부분 제외)
 * 예: "2023-03-15T14:30:00Z" -> "2023-03-15"
 */
const extractDateOnly = (dateTimeString: string): string => {
  return formatDate(dateTimeString, true, true);
};

/**
 * API의 payment_type을 UI에서 사용하는 형식으로 변환
 */
const mapPaymentType = (paymentType: string | undefined): string => {
  if (!paymentType) return '즉시결제';

  const paymentTypeMap: Record<string, string> = {
    'immediate': '즉시결제',
    '10_days': '10일',
    '20_days': '20일',
    '30_days': '30일',
    '40_days': '40일',
    '50_days': '50일',
    '60_days': '60일',
  };

  return paymentTypeMap[paymentType] || '즉시결제';
};

/**
 * Product를 CartItem으로 변환하는 함수
 */
const mapProductToCartItem = (
  product: CartItem,
  index: number,
): CartProduct => {
  const baseProduct = mapProductApiToModel(product, index);

  return {
    ...baseProduct,
    stock: product.stock
      ? {
        quantity: product.stock,
        formatted: `${product.stock.toLocaleString()}`,
      }
      : {
        quantity: product.quantity || 0,
        formatted: product.quantity?.toString() || 'x',
      },
    date: extractDateOnly(product.created_at || new Date().toISOString()),
    companyName: product.sellers?.[0]?.company || '-',
    companyAddress: product.sellers?.[0]?.address || '-',
    managers:
      product.sellers?.map((seller) => ({
        name: seller.name || '-',
        phone: seller.phone_number || '-',
      })) || [],
    purchaseQuantity: product.quantity || 0,
    paymentType: mapPaymentType(product.payment_type),
  };
};

/**
 * CartResponse를 CartModel 배열로 변환하는 함수
 * created_at(날짜 부분만) 기준으로 그룹화
 */
export const mapCartResponseToCartModel = (
  response: CartResponse,
): CartGroup[] => {
  if (
    !response ||
    !response.cart_items ||
    !Array.isArray(response.cart_items)
  ) {
    return [];
  }

  // 날짜별로 아이템 그룹화
  const groupedByDate: Record<string, CartProduct[]> = {};

  response.cart_items.forEach((product, index) => {
    const dateAdded = product.created_at || new Date().toISOString();
    const dateKey = extractDateOnly(dateAdded);

    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = [];
    }

    groupedByDate[dateKey].push(mapProductToCartItem(product, index));
  });

  // 그룹화된 데이터를 CartGroup 배열로 변환
  const cartGroups: CartGroup[] = Object.entries(groupedByDate).map(
    ([date, items]) => ({
      date,
      cartProducts: items,
    }),
  );

  // 날짜 내림차순으로 정렬 (최근 추가된 항목이 먼저 오도록)
  return cartGroups.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
};
